import { __decorate } from "tslib";
import { Watch, Component, Prop } from 'vue-property-decorator';
import { Getter, Mutation } from 'vuex-class';
import moment from 'moment';
import { selectOptionBuilder } from '@/helpers/select_option_builder';
import ViewportSizeMixin from '@/helpers/mixins/ViewportSizeMixin';
import BaseTextField from '@/components/v2/BaseTextField.vue';
import BaseTextArea from '@/components/v2/BaseTextArea';
import BaseFileInput from '@/components/v2/BaseFileInput.vue';
import ActionButton from '@/components/action_button/Index.vue';
import DateTimeField from '@/components/datetime_field/Index.vue';
import { mixins } from 'vue-class-component';
import BroadcastCreateMutation from '@/plugins/graphql/mutations/v2/broadcast-create.gql';
import BroadcastUpdateMutation from '@/plugins/graphql/mutations/v2/broadcast-update.gql';
import BroadcastsQuery from '@/plugins/graphql/queries/v2/broadcasts.gql';
import AppSegmentations from '@/plugins/graphql/queries/v2/app-segmentations-basic.gql';
import { required, maxLength } from 'vuelidate/lib/validators';
import BroadcastMessageFragment from '@/plugins/graphql/fragments/v2/broadcast-message.gql';
import { BroadcastTarget } from '@/helpers/entities/enums/v2/BroadcastTarget';
import RouterParamsAppMixin from '@/helpers/vue_mixins/RouterParamsAppMixin';
import FormMixin from '@/helpers/vue_mixins/FormMixin';
import FormValidationsMixin from '@/helpers/vue_mixins/v2/FormValidationsMixin';
import { BROADCAST } from '@/helpers/constants/Store';
import { MAX_FILE_SIZE } from '@/helpers/constants/app';
import { BroadcastStatus } from '@/helpers/entities/enums/BroadcastStatus';
import { BroadcastType } from '@/helpers/entities/enums/BroadcastType';
import { BroadcastCategory } from '@/helpers/entities/enums/BroadcastCategory';
import { BroadcastMessageType } from '@/helpers/entities/enums/v2/BroadcastMessageType';
import CleverTapEventsMixin from '@/helpers/vue_mixins/v3/CleverTapEventsMixin';
const maxCharacters = 2000;
const sizeValidator = (file) => {
    if (file) {
        return file.size < MAX_FILE_SIZE;
    }
    return true;
};
let BroadcastForm = class BroadcastForm extends mixins(RouterParamsAppMixin, FormMixin, FormValidationsMixin, ViewportSizeMixin, CleverTapEventsMixin) {
    constructor() {
        super(...arguments);
        this.appSegmentationUuid = null;
        this.appSegmentations = [];
        this.displayOneTimeNotifForm = false;
        this.followUpMedia = null;
        this.broadcastForm = {
            appUuid: null,
            message: '',
            target: this.target,
            title: '',
            category: BroadcastCategory.announcement,
            appSegmentationUuid: this.appSegmentationUuid,
            broadcastType: BroadcastType.to_all,
            broadcastDate: '',
            messageType: this.messageType,
            notificationHeader: '',
            notificationDescription: '',
            followUpDate: '',
            followUpDescription: '',
            followUpTitle: '',
        };
    }
    get messageTypes() {
        return [
            selectOptionBuilder('text', 'Text'),
            selectOptionBuilder('one_time_notification', 'One Time Notification'),
        ];
    }
    get broadcastDatePreviousDay() {
        return moment(this.broadcastForm.broadcastDate).subtract(1, 'days').toISOString();
    }
    get yesterday() {
        return moment().subtract(1, 'days').toISOString();
    }
    onToggleFormChanged() {
        if (this.broadcastDetailsFields) {
            this.appSegmentationUuid = this.currentAppSegmentation.value;
            const { message, title, messageType } = this.broadcastDetailsFields;
            const { appSegmentationUuid } = this;
            const broadcastDate = this.broadcastDetailsFields.broadcastDateISO;
            this.broadcastForm = {
                ...this.broadcastForm,
                message,
                title,
                messageType,
                appSegmentationUuid,
                broadcastDate,
            };
            this.populateOneTimeFields();
        }
        else {
            this.resetForm();
            this.displayOneTimeNotifForm = false;
            this.broadcastForm.broadcastDate = moment().toISOString();
            this.broadcastForm.followUpDate = moment().add(1, 'minutes').toISOString();
            this.broadcastForm.messageType = this.messageType;
        }
    }
    populateOneTimeFields() {
        if (this.broadcastDetailsFields) {
            const { notificationHeader, followUpTitle, followUpDescription, followUpDate, } = this.broadcastDetailsFields;
            this.displayOtnForm();
            this.broadcastForm = {
                ...this.broadcastForm,
                notificationHeader: notificationHeader || '',
                followUpTitle: followUpTitle || '',
                followUpDescription: followUpDescription || '',
                followUpDate: !followUpDate ? '' : this.broadcastDetailsFields.followUpDateISO,
            };
        }
    }
    displayOtnForm() {
        if (this.broadcastForm.messageType === 'one_time_notification') {
            this.displayOneTimeNotifForm = true;
        }
        else {
            this.displayOneTimeNotifForm = false;
        }
    }
    get formTitle() {
        const prefix = this.broadcastDetailsFields ? 'Update' : 'Add';
        const target = this.target === BroadcastTarget.Messenger ? 'Facebook' : 'SMS';
        return `${prefix} ${target} Broadcast`;
    }
    get currentAppSegmentation() {
        if (this.broadcastDetailsFields.appSegmentation) {
            return {
                value: this.broadcastDetailsFields.appSegmentation.uuid,
                text: this.broadcastDetailsFields.appSegmentation.name,
            };
        }
        return [...this.appSegmentations].pop();
    }
    // mixin getters are not initialized via local data
    get commonInput() {
        const date = new Date(this.broadcastForm.broadcastDate);
        const followDate = this.broadcastForm.messageType === BroadcastMessageType.OTN
            ? new Date(this.broadcastForm.followUpDate)
            : '';
        const notifDescrip = this.broadcastForm.message;
        return {
            ...this.broadcastForm,
            broadcastType: this.appSegmentationUuid ? BroadcastType.by_segment : BroadcastType.to_all,
            broadcastDate: date,
            appSegmentationUuid: this.appSegmentationUuid,
            appUuid: this.appUUID,
            followUpDate: followDate,
            notificationDescription: notifDescrip,
            followUpMedia: [this.followUpMedia],
        };
    }
    get target() {
        return this.broadcastQueryInputs.target;
    }
    get messageType() {
        return this.broadcastQueryInputs.messageType || this.messageTypes[0];
    }
    get isSms() {
        return this.target === BroadcastTarget.SMS;
    }
    get isOtn() {
        return this.messageType === BroadcastMessageType.OTN;
    }
    fileName(file) {
        return (!!file && file.name) || null;
    }
    /**
     * Broadcast Message Limi
     * FB has 2000 character limit while SMS has no limit
     * @author Peter John Alvarado <peterjohn@gorated.ph>
     * @since Version {0.16.0}
     * @see https://developers.facebook.com/docs/messenger-platform/reference/send-api/
     *
     * @return {number} message limit
     */
    get messageLimit() {
        return maxCharacters;
    }
    onHide() {
        this.hideForm();
        this.$emit('formCancelled');
        this.appSegmentationUuid = null;
    }
    async submitForm() {
        let mutation;
        let message;
        if (this.broadcastDetailsFields) {
            this.broadcastAction('SUBMIT EDIT');
            mutation = this.update;
            message = 'updated';
        }
        else {
            this.broadcastAction('SUBMIT CREATE');
            mutation = this.create;
            message = 'created';
        }
        this.$v.$touch();
        if (!this.$v.$invalid) {
            await mutation()
                .then(() => {
                this.successNotification(`Broadcast ${message}`);
            })
                .catch((e) => {
                this.errorNotification(e.message);
                this.logError(e.message);
            })
                .finally(() => {
                this.$emit('formSaved');
                this.onHide();
            });
        }
    }
    async create() {
        await this.$apollo.mutate({
            mutation: BroadcastCreateMutation,
            variables: { input: this.commonInput },
            update: (cache, { data }) => {
                const broadcastQuery = {
                    query: BroadcastsQuery,
                    variables: {
                        ...this.broadcastQueryInputs,
                        status: BroadcastStatus.sending,
                    },
                };
                const currentBroadcasts = cache.readQuery(broadcastQuery).broadcasts;
                const newBroadcasts = this.newBroadcasts(currentBroadcasts, data.broadcastCreate.broadcast);
                cache.writeQuery({
                    ...broadcastQuery,
                    data: { broadcasts: newBroadcasts },
                });
            },
        });
    }
    async update() {
        const broadcastUuid = this.broadcastDetailsFields.uuid;
        await this.$apollo.mutate({
            mutation: BroadcastUpdateMutation,
            variables: {
                input: {
                    ...this.commonInput,
                    broadcastUuid: this.broadcastDetailsFields.uuid,
                },
            },
            update: (cache, { data }) => {
                const broadcastFragment = {
                    id: broadcastUuid,
                    fragment: BroadcastMessageFragment,
                    fragmentName: 'BroadcastMessage',
                };
                const { broadcast } = data.broadcastUpdate;
                broadcast.uuid = broadcastUuid;
                cache.writeFragment({
                    ...broadcastFragment,
                    data: broadcast,
                });
            },
        });
    }
    newBroadcasts(broadcasts, broadcast) {
        const edges = Object.assign([], broadcasts.edges);
        const counterCacheIterator = 1;
        if (broadcast.status === this.broadcastQueryInputs.status) {
            edges.push({
                node: broadcast,
                __typename: 'BroadcastMessageEdge',
            });
            broadcasts.totalCount += counterCacheIterator;
        }
        return {
            ...broadcasts,
            edges,
        };
    }
};
__decorate([
    Prop({ type: Object, required: true })
], BroadcastForm.prototype, "broadcastQueryInputs", void 0);
__decorate([
    Prop({ type: Object, default: null })
], BroadcastForm.prototype, "broadcastDetailsFields", void 0);
__decorate([
    Getter('toggleForm', { namespace: BROADCAST })
], BroadcastForm.prototype, "toggleForm", void 0);
__decorate([
    Mutation('hideForm', { namespace: BROADCAST })
], BroadcastForm.prototype, "hideForm", void 0);
__decorate([
    Watch('toggleForm')
], BroadcastForm.prototype, "onToggleFormChanged", null);
BroadcastForm = __decorate([
    Component({
        components: {
            ActionButton,
            BaseTextField,
            BaseTextArea,
            BaseFileInput,
            DateTimeField,
        },
        validations() {
            const validationObj = {
                followUpMedia: {},
                broadcastForm: {
                    title: {},
                    message: {
                        required,
                        maxLength: maxLength(maxCharacters),
                    },
                    messageType: { required },
                    notificationHeader: {},
                    notificationDescription: {},
                    followUpDate: {},
                    followUpDescription: {},
                    followUpTitle: {},
                },
            };
            const vm = this;
            if (vm.isSms) {
                return Object.assign(validationObj, {
                    broadcastForm: {
                        ...validationObj.broadcastForm,
                        title: { required },
                    },
                });
            }
            if (vm.displayOneTimeNotifForm) {
                return Object.assign(validationObj, {
                    followUpMedia: {
                        required: () => true,
                        maxFileSizeExceeded: sizeValidator,
                    },
                    broadcastForm: {
                        ...validationObj.broadcastForm,
                        message: {},
                        notificationHeader: { required },
                        followUpDate: { required },
                        followUpDescription: { required },
                        followUpTitle: { required },
                    },
                });
            }
            return validationObj;
        },
        apollo: {
            appSegmentations: {
                query: AppSegmentations,
                variables() {
                    return { appUuid: this.appUUID };
                },
                update({ appSegmentations }) {
                    const nodeOnly = ({ node }) => ({
                        value: node.uuid,
                        text: node.name,
                    });
                    const appSegmentationEdges = appSegmentations.edges.map(nodeOnly);
                    appSegmentationEdges.push({
                        value: null,
                        text: 'All',
                    });
                    return appSegmentationEdges;
                },
            },
        },
    })
], BroadcastForm);
export default BroadcastForm;
